import React from 'react';
import styled from '@emotion/styled';
import type { GuardianDiagnostics } from '../authentication/GuardianProvider';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 48px 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: #002677;
  margin-bottom: 16px;
`;

const Message = styled.p`
  font-size: 16px;
  color: #5a6c7d;
  max-width: 520px;
  line-height: 1.5;
`;

const NextStep = styled.p`
  font-size: 15px;
  color: #002677;
  max-width: 520px;
  line-height: 1.5;
  margin-top: 16px;
  padding: 14px 18px;
  background: #eef4ff;
  border: 1px solid #c7d9f5;
  border-radius: 6px;
`;

const ErrorDetail = styled.p`
  font-size: 14px;
  color: #c40000;
  max-width: 520px;
  line-height: 1.4;
  margin-top: 12px;
  padding: 12px 16px;
  background: #fff5f5;
  border-radius: 6px;
  border: 1px solid #fecaca;
`;

const Diagnostics = styled.details`
  margin-top: 20px;
  max-width: 520px;
  text-align: left;
  font-size: 13px;
  color: #5a6c7d;
  background: #f4f6f8;
  border: 1px solid #e1e8ed;
  border-radius: 6px;
  padding: 12px 16px;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: #002677;
  }

  dt {
    font-weight: 600;
    margin-top: 8px;
  }

  dd {
    margin: 2px 0 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    word-break: break-word;
  }
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: #002677;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #001a54;
  }
`;

interface AccessDeniedPageProps {
  error?: string | null;
  diagnostics?: GuardianDiagnostics | null;
  onRetry?: () => void;
  /** The server could not complete Sentinel or Guardian verification. */
  serviceUnavailable?: boolean;
}

const formatList = (values: string[]): string => (values.length ? values.join(', ') : '(none)');

/**
 * Shown when Sentinel authenticated the user but Guardian granted no
 * qualifying role.
 *
 * The diagnostics block is deliberate: the most common cause of a denial here
 * is either no role assigned for the HPS tenant, or a mismatch between the role
 * names Guardian is provisioned with and the ones this app expects. Showing
 * both sides turns a support ticket into a glance.
 */
export const AccessDeniedPage: React.FC<AccessDeniedPageProps> = ({
  error,
  diagnostics,
  onRetry,
  serviceUnavailable = false,
}) => (
  <Container>
    <Title>{serviceUnavailable ? 'Authentication Service Unavailable' : 'Access Denied'}</Title>
    <Message>
      {serviceUnavailable
        ? 'The application could not complete your authentication check. Please try again shortly.'
        : 'You signed in successfully, but you do not have a Guardian role that grants access to the HPS AI Assistant.'}
    </Message>

    {diagnostics?.accessRequestHint && (
      <NextStep>
        <strong>To get access:</strong> {diagnostics.accessRequestHint}
      </NextStep>
    )}

    {error && <ErrorDetail>{error}</ErrorDetail>}

    {diagnostics && (
      <Diagnostics>
        <summary>Details for your administrator</summary>
        <dl>
          <dt>Roles Guardian returned for you</dt>
          <dd>{formatList(diagnostics.rolesReturned)}</dd>
          <dt>Permissions Guardian returned for you</dt>
          <dd>{formatList(diagnostics.permissionsReturned)}</dd>
          <dt>Roles this application accepts</dt>
          <dd>{formatList(diagnostics.expectedRoles)}</dd>
          <dt>Permission this application accepts</dt>
          <dd>{diagnostics.expectedPermission || '(none)'}</dd>
        </dl>
      </Diagnostics>
    )}

    {onRetry && <RetryButton onClick={onRetry}>Retry</RetryButton>}
  </Container>
);
